import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  Container,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Card,
  CardBody,
  Badge,
  Row,
  Col,
  Alert,
  Spinner,
  Table,
  ButtonGroup,
  FormGroup,
  Label,
  Input
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { useDispatch, useSelector } from "react-redux";
import { fetchSettlementsRequest } from "../../store/settlementStatements/actions";
import TableContainer from "components/Common/TableContainer";
import { format, parseISO, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays } from 'date-fns';
import { ar } from 'date-fns/locale';
import { FiExternalLink, FiDownload, FiFileText, FiFile } from "react-icons/fi";
import { useReactToPrint } from 'react-to-print';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const SettlementStatements = () => {
  const dispatch = useDispatch();
  const { settlements, loading, error } = useSelector((state) => state.settlements);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Excel Export Function for All Settlements
  const exportToExcel = () => {
    if (groupedSettlements.length === 0) {
      alert('لا توجد بيانات لتصديرها');
      return;
    }

    const wb = XLSX.utils.book_new();
    
    // Sheet 1: Detailed Settlement Summary
    const summaryData = groupedSettlements.map((settlement, idx) => ({
      '#': idx + 1,
      'اسم مقدم الخدمة': settlement.service_provider?.name || 'غير متوفر',
      'اسم العميل': settlement.customer?.name || 'غير متوفر',
      'نوع التسوية': settlement.settlement_type === 'refund' ? 'استرجاع' : 'عادية',
      'رقم الحجز': settlement.booking_id || 'غير متوفر',
      'رقم الفاتورة': settlement.invoice_id || 'غير متوفر',
      'المبلغ الأصلي': settlement.amounts?.original_service_amount || settlement.amounts?.gross_service_amount || 0,
      'عمولة المنصة': settlement.amounts?.platform_commission || 0,
      'عمولة تمارا': settlement.amounts?.tamara_commission || 0,
      'المبلغ بعد العمولة': settlement.amounts?.amount_after_commission || settlement.amounts?.service_net || 0,
      'ضريبة ق.م (15%)': settlement.amounts?.service_vat || 0,
      'الصافي مع الضريبة': settlement.amounts?.net_amount_with_vat || 0,
      'غرامات الإلغاء': settlement.amounts?.cancellation_fines || 0,
      'سبب الخصم': settlement.fine_reason || 'بلا',
      'المبلغ النهائي': settlement.amounts?.final_payout || 0,
      'المحتسب كإيراد': settlement.amounts?.revenue_counted_amount || 0,
      'التاريخ': settlement.created_at ? format(parseISO(settlement.created_at), 'yyyy-MM-dd HH:mm') : 'غير متوفر',
      'الحالة': settlement.status === 'generated' ? 'تم الإنشاء' : settlement.status,
      'تسوية مجمعة': settlement.is_batched ? 'نعم' : 'لا',
      'عدد الفواتير': settlement.invoice_count || 1
    }));

    const ws1 = XLSX.utils.json_to_sheet(summaryData);
    ws1['!rtl'] = true;
    
    // Set column widths
    ws1['!cols'] = [
      { wch: 4 },   // #
      { wch: 18 },  // Service Provider
      { wch: 18 },  // Customer
      { wch: 10 },  // Settlement Type
      { wch: 15 },  // Booking ID
      { wch: 15 },  // Invoice ID
      { wch: 12 },  // Original Amount
      { wch: 12 },  // Commission
      { wch: 12 },  // Tamara Commission
      { wch: 12 },  // After Commission
      { wch: 12 },  // VAT
      { wch: 12 },  // With VAT
      { wch: 12 },  // Fines
      { wch: 15 },  // Fine Reason
      { wch: 12 },  // Final Payout
      { wch: 12 },  // Revenue Counted
      { wch: 20 },  // Date
      { wch: 10 },  // Status
      { wch: 10 },  // Batched
      { wch: 10 }   // Invoice Count
    ];

    XLSX.utils.book_append_sheet(wb, ws1, 'ملخص التسويات');

    // Sheet 2: Financial Summary
    const totalOriginal = groupedSettlements.reduce((sum, s) => sum + (s.amounts?.original_service_amount || s.amounts?.gross_service_amount || 0), 0);
    const totalCommission = groupedSettlements.reduce((sum, s) => sum + (s.amounts?.platform_commission || 0), 0);
    const totalTamaraCommission = groupedSettlements.reduce((sum, s) => sum + (s.amounts?.tamara_commission || 0), 0);
    const totalAfterCommission = groupedSettlements.reduce((sum, s) => sum + (s.amounts?.amount_after_commission || s.amounts?.service_net || 0), 0);
    const totalVAT = groupedSettlements.reduce((sum, s) => sum + (s.amounts?.service_vat || 0), 0);
    const totalWithVAT = groupedSettlements.reduce((sum, s) => sum + (s.amounts?.net_amount_with_vat || 0), 0);
    const totalFines = groupedSettlements.reduce((sum, s) => sum + (s.amounts?.cancellation_fines || 0), 0);
    const totalFinalPayout = groupedSettlements.reduce((sum, s) => sum + (s.amounts?.final_payout || 0), 0);
    const totalRevenueCounted = groupedSettlements.reduce(
      (sum, s) =>
        sum +
        (s.amounts?.revenue_counted_amount !== undefined
          ? Number(s.amounts?.revenue_counted_amount || 0)
          : (s.settlement_type === 'refund' ? 0 : Number(s.amounts?.final_payout || 0))),
      0
    );

    const financialSummary = [
      { 'البيان': 'عدد التسويات', 'القيمة': groupedSettlements.length },
      { 'البيان': 'إجمالي المبالغ الأصلية', 'القيمة': totalOriginal.toFixed(2) },
      { 'البيان': 'إجمالي عمولات المنصة', 'القيمة': totalCommission.toFixed(2) },
      { 'البيان': 'إجمالي عمولة تمارا', 'القيمة': totalTamaraCommission.toFixed(2) },
      { 'البيان': 'إجمالي المبالغ بعد العمولة', 'القيمة': totalAfterCommission.toFixed(2) },
      { 'البيان': 'إجمالي ضرائب ق.م', 'القيمة': totalVAT.toFixed(2) },
      { 'البيان': 'إجمالي المبالغ مع الضريبة', 'القيمة': totalWithVAT.toFixed(2) },
      { 'البيان': 'إجمالي الغرامات', 'القيمة': totalFines.toFixed(2) },
      { 'البيان': 'إجمالي المبالغ النهائية', 'القيمة': totalFinalPayout.toFixed(2) },
      { 'البيان': 'إجمالي المحتسب كإيراد (بدون الاسترجاعات)', 'القيمة': totalRevenueCounted.toFixed(2) },
      { 'البيان': 'تاريخ التقرير', 'القيمة': format(new Date(), 'yyyy-MM-dd HH:mm') }
    ];

    const ws2 = XLSX.utils.json_to_sheet(financialSummary);
    ws2['!rtl'] = true;
    ws2['!cols'] = [{ wch: 30 }, { wch: 15 }];
    
    XLSX.utils.book_append_sheet(wb, ws2, 'الملخص المالي');

    // Save workbook
    XLSX.writeFile(wb, `Settlement_Statements_${format(new Date(), 'yyyy-MM-dd_HHmm')}.xlsx`);
  };

  // Excel Export for Individual Settlement
  const exportSettlementToExcel = (settlement) => {
    const wb = XLSX.utils.book_new();

    // Sheet 1: Settlement Details
    const settlementData = [
      ['الملف التفصيلي للتسوية'],
      [],
      ['معلومات النظام'],
      ['رقم التسوية', settlement.id?.slice(0, 12) || 'N/A'],
      ['رقم الفاتورة', settlement.invoice_id || 'غير متوفر'],
      ['رقم الحجز', settlement.booking_id || 'غير متوفر'],
      ['التاريخ', settlement.created_at ? format(parseISO(settlement.created_at), 'yyyy-MM-dd HH:mm') : 'غير متوفر'],
      ['الحالة', settlement.status === 'generated' ? 'تم الإنشاء' : settlement.status],
      ['نوع التسوية', settlement.settlement_type === 'refund' ? 'استرجاع' : 'عادية'],
      [],
      ['معلومات مقدم الخدمة'],
      ['الاسم', settlement.service_provider?.name || 'غير متوفر'],
      ['السجل التجاري', settlement.service_provider?.commercial_register || 'غير متوفر'],
      ['رقم الضريبة', settlement.service_provider?.vat_number || 'غير متوفر'],
      ['الهاتف', settlement.service_provider?.mobile || 'غير متوفر'],
      ['البلد', settlement.service_provider?.country || 'غير متوفر'],
      [],
      ['معلومات العميل'],
      ['الاسم', settlement.customer?.name || 'غير متوفر'],
      ['الهاتف', settlement.customer?.mobile || 'غير متوفر'],
      [],
      ['الملخص المالي'],
      ['المبلغ الأصلي للخدمة', settlement.amounts?.original_service_amount || settlement.amounts?.gross_service_amount || 0],
      ['عمولة المنصة', settlement.amounts?.platform_commission || 0],
      ['عمولة تمارا', settlement.amounts?.tamara_commission || 0],
      ['المبلغ بعد العمولة (قبل ض.ق.م)', settlement.amounts?.amount_after_commission || settlement.amounts?.service_net || 0],
      ['ضريبة القيمة المضافة (15%)', settlement.amounts?.service_vat || 0],
      ['الصافي مع الضريبة', settlement.amounts?.net_amount_with_vat || 0],
      ['غرامات الإلغاء', settlement.amounts?.cancellation_fines || 0],
      ['سبب الخصم', settlement.fine_reason || 'بلا'],
      ['المبلغ النهائي المستحق', settlement.amounts?.final_payout || 0],
      ['المحتسب كإيراد', settlement.amounts?.revenue_counted_amount || 0],
      ['مبلغ الاسترجاع', settlement.amounts?.refund_amount || 0]
    ];

    const ws1 = XLSX.utils.aoa_to_sheet(settlementData);
    ws1['!rtl'] = true;
    ws1['!cols'] = [{ wch: 30 }, { wch: 20 }];
    
    // Style header
    ws1['A1'].font = { bold: true, size: 14 };
    
    XLSX.utils.book_append_sheet(wb, ws1, 'تفاصيل التسوية');

    // Sheet 2: Deductions (if any)
    if (Array.isArray(settlement.deductions) && settlement.deductions.length > 0) {
      const deductionsData = settlement.deductions.map(ded => ({
        'النوع': ded.type === 'cancellation_fine' ? 'غرامة إلغاء' : ded.type || 'خصم',
        'السبب': ded.reason || settlement.fine_reason || 'غير محدد',
        'المبلغ': ded.amount
      }));

      const ws2 = XLSX.utils.json_to_sheet(deductionsData);
      ws2['!rtl'] = true;
      ws2['!cols'] = [{ wch: 15 }, { wch: 25 }, { wch: 12 }];
      
      XLSX.utils.book_append_sheet(wb, ws2, 'الخصومات');
    }

    // Save workbook
    XLSX.writeFile(wb, `Settlement_${settlement.id?.slice(0, 8) || 'Detail'}_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
  };

  // Fetch settlements on component mount
  useEffect(() => {
    dispatch(fetchSettlementsRequest());
  }, [dispatch]);

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const viewDetails = (settlement) => {
    setSelectedSettlement(settlement);
    toggleModal();
  };

  // Date filtering functions
  const handleQuickFilter = (type) => {
    const today = new Date();
    const yesterday = subDays(today, 1);
    
    switch(type) {
      case 'today':
        setStartDate(format(today, 'yyyy-MM-dd'));
        setEndDate(format(today, 'yyyy-MM-dd'));
        break;
      case 'yesterday':
        setStartDate(format(yesterday, 'yyyy-MM-dd'));
        setEndDate(format(yesterday, 'yyyy-MM-dd'));
        break;
      case 'thisWeek':
        setStartDate(format(startOfWeek(today, { weekStartsOn: 6 }), 'yyyy-MM-dd'));
        setEndDate(format(today, 'yyyy-MM-dd'));
        break;
      case 'reset':
        setStartDate(null);
        setEndDate(null);
        break;
      default:
        break;
    }
  };

  const isDateInRange = (dateStr) => {
    if (!dateStr || (!startDate && !endDate)) return true;
    
    try {
      const date = typeof dateStr === 'string' ? parseISO(dateStr) : new Date(dateStr);
      const dateOnly = format(date, 'yyyy-MM-dd');
      
      if (startDate && endDate) {
        return dateOnly >= startDate && dateOnly <= endDate;
      } else if (startDate) {
        return dateOnly >= startDate;
      } else if (endDate) {
        return dateOnly <= endDate;
      }
      return true;
    } catch (error) {
      console.error('Date filter error:', error);
      return true;
    }
  };

  const formatDate = (date) => {
    if (!date) return 'غير متوفر';
    try {
      const parsedDate = typeof date === 'string' ? parseISO(date) : date;
      return format(parsedDate, 'yyyy/MM/dd - hh:mm a', { locale: ar });
    } catch (error) {
      console.error('Date parsing error:', error);
      return 'تاريخ غير صالح';
    }
  };

  const formatCurrency = (value) => {
    return `${(parseFloat(value) || 0).toFixed(2)} ر.س`;
  };

const groupedSettlements = useMemo(() => {
  const list = Array.isArray(settlements) ? settlements : [];
  const groups = new Map();

  const toNumber = (value) => Number(value || 0);
  const toDate = (value) => {
    try {
      if (!value) return new Date(0);
      return typeof value === 'string' ? new Date(value) : new Date(value);
    } catch {
      return new Date(0);
    }
  };

  list.forEach((settlement) => {
    const batchId = settlement.batch_settlement_id;

    if (batchId) {
      const key = `batch-${batchId}`;
      const current = groups.get(key);
      const amounts = settlement.amounts || {};
      const createdAt = toDate(settlement.created_at);

      if (!current) {
        // FIRST item in the batch → take invoice_count from here
        groups.set(key, {
          ...settlement,
          is_batched: true,
          invoice_count: settlement.invoice_count || 1,
          customer: { name: "متعدد العملاء", mobile: null },
          amounts: {
            original_service_amount: toNumber(amounts.original_service_amount),
            platform_commission: toNumber(amounts.platform_commission),
            amount_after_commission: toNumber(amounts.amount_after_commission),
            tamara_commission: toNumber(amounts.tamara_commission),
            service_vat: toNumber(amounts.service_vat),
            net_amount_with_vat: toNumber(amounts.net_amount_with_vat),
            cancellation_fines: toNumber(amounts.cancellation_fines),
            final_payout: toNumber(amounts.final_payout),
            revenue_counted_amount: toNumber(
              amounts.revenue_counted_amount !== undefined
                ? amounts.revenue_counted_amount
                : (settlement.settlement_type === 'refund' ? 0 : amounts.final_payout)
            ),
            refund_amount: toNumber(
              amounts.refund_amount !== undefined
                ? amounts.refund_amount
                : (settlement.settlement_type === 'refund' ? amounts.final_payout : 0)
            ),
            gross_service_amount: toNumber(amounts.gross_service_amount),
            service_net: toNumber(amounts.service_net),
            net_after_fines: toNumber(amounts.net_after_fines ?? amounts.service_net),
          },
          batch_invoice_ids: settlement.invoice_id ? [settlement.invoice_id] : [],
          _date: createdAt,
        });
      } else {
        // Aggregate ONLY amounts
        current.amounts.original_service_amount += toNumber(amounts.original_service_amount);
        current.amounts.platform_commission += toNumber(amounts.platform_commission);
        current.amounts.tamara_commission += toNumber(amounts.tamara_commission);
        current.amounts.amount_after_commission += toNumber(amounts.amount_after_commission);
        current.amounts.service_vat += toNumber(amounts.service_vat);
        current.amounts.net_amount_with_vat += toNumber(amounts.net_amount_with_vat);
        current.amounts.cancellation_fines += toNumber(amounts.cancellation_fines);
        current.amounts.final_payout += toNumber(amounts.final_payout);
        current.amounts.revenue_counted_amount += toNumber(
          amounts.revenue_counted_amount !== undefined
            ? amounts.revenue_counted_amount
            : (settlement.settlement_type === 'refund' ? 0 : amounts.final_payout)
        );
        current.amounts.refund_amount += toNumber(
          amounts.refund_amount !== undefined
            ? amounts.refund_amount
            : (settlement.settlement_type === 'refund' ? amounts.final_payout : 0)
        );
        current.amounts.gross_service_amount += toNumber(amounts.gross_service_amount);
        current.amounts.service_net += toNumber(amounts.service_net);
        current.amounts.net_after_fines += toNumber(amounts.net_after_fines ?? amounts.service_net);

        // ❌ invoice_count is NOT touched anymore

        if (settlement.invoice_id) {
          current.batch_invoice_ids = Array.from(
            new Set([...(current.batch_invoice_ids || []), settlement.invoice_id])
          );
        }

        if (createdAt > (current._date || new Date(0))) {
          current._date = createdAt;
          current.created_at = settlement.created_at;
        }

        groups.set(key, current);
      }
    } else {
      groups.set(`single-${settlement.id}`, settlement);
    }
  });

  const allResults = Array.from(groups.values()).map(({ _date, ...rest }) => rest);
  
  // Apply date filter
  return allResults.filter(settlement => isDateInRange(settlement.created_at));
}, [settlements, startDate, endDate, isDateInRange]);


  const columns = useMemo(
    () => [
      {
        Header: "#",
        accessor: "index",
        Cell: ({ row }) => row.index + 1,
        width: 50
      },
      {
        Header: "اسم مقدم الخدمة",
        accessor: "service_provider.name",
        Cell: ({ value }) => value || "غير متوفر",
      },
      {
        Header: "اسم العميل",
        accessor: "customer.name",
        Cell: ({ value }) => value || "غير متوفر",
      },
      {
        Header: "نوع التسوية",
        accessor: "settlement_type",
        Cell: ({ value }) => (
          <Badge color={value === "refund" ? "danger" : "success"}>
            {value === "refund" ? "استرجاع" : "عادية"}
          </Badge>
        ),
      },
      {
        Header: "المبلغ الأصلي",
        accessor: "amounts.original_service_amount",
        Cell: ({ value, row }) => {
          // Use new field if available, fallback to legacy
          const amount = value || row.original.amounts?.gross_service_amount || 0;
          return formatCurrency(amount);
        },
      },
      {
        Header: "عمولة المنصة",
        accessor: "amounts.platform_commission",
        Cell: ({ value }) => {
          const commission = Number(value || 0);
          return commission > 0 ? (
            <Badge color="warning">-{commission.toFixed(2)} ر.س</Badge>
          ) : (
            <Badge color="light" className="text-muted">0.00 ر.س</Badge>
          );
        },
      },
      {
        Header: "بعد العمولة (قبل ض.ق.م)",
        accessor: "amounts.amount_after_commission",
        Cell: ({ value, row }) => {
          const amount = value || row.original.amounts?.service_net || 0;
          return formatCurrency(amount);
        },
      },
      {
        Header: "ضريبة ق.م (15%)",
        accessor: "amounts.service_vat",
        Cell: ({ value }) => formatCurrency(value),
      },
      {
        Header: "الصافي مع ض.ق.م",
        accessor: "amounts.net_amount_with_vat",
        Cell: ({ value, row }) => {
          const amount = value || (row.original.amounts?.amount_after_commission || 0) * 1.15;
          return formatCurrency(amount);
        },
      },
      {
        Header: "غرامات الإلغاء",
        accessor: "amounts.cancellation_fines",
        Cell: ({ value }) => {
          const fine = Number(value || 0);
          return fine > 0 ? (
            <Badge color="danger">-{fine.toFixed(2)} ر.س</Badge>
          ) : (
            <Badge color="light" className="text-muted">لا يوجد</Badge>
          );
        },
      },
      {
        Header: "سبب الخصم",
        accessor: "fine_reason",
        Cell: ({ value }) => value || "لا يوجد",
      },
      {
        Header: "الغرامة خصمت من",
        accessor: "fine_charged_to",
        Cell: ({ value, row }) => {
          const chargedTo = value || row.original.cancelled_by || "مقدم الخدمة";
          return (
            <Badge color={chargedTo === "app_user" ? "info" : "warning"}>
              {chargedTo === "app_user" ? "العميل" : "مقدم الخدمة"}
            </Badge>
          );
        },
      },
      {
        Header: "المبلغ النهائي",
        accessor: "amounts.final_payout",
        Cell: ({ value, row }) => {
          // Use new final_payout if available, fallback to legacy calculation
          const finalAmount = value !== undefined ? value : (
            (row.original.amounts?.net_amount_with_vat || row.original.amounts?.net_after_fines || 0) - 
            (row.original.amounts?.cancellation_fines || 0)
          );
          const isRefund = row.original.settlement_type === "refund" || row.original.is_refund_settlement;
          if (isRefund) {
            return (
              <div>
                <strong className="text-danger">{formatCurrency(finalAmount)}</strong>
                <div><small className="text-muted">استرجاع (غير محتسب كإيراد)</small></div>
              </div>
            );
          }
          return <strong className="text-success">{formatCurrency(finalAmount)}</strong>;
        },
      },
      {
        Header: "التاريخ",
        accessor: "created_at",
        Cell: ({ value }) => formatDate(value),
      },
      {
        Header: "الحالة",
        accessor: "status",
        Cell: ({ value, row }) => {
          const isRefund = row.original.settlement_type === "refund" || row.original.is_refund_settlement;
          return (
            <Badge color={isRefund ? "danger" : "success"} className="p-2">
              {isRefund ? "استرجاع" : value === "generated" ? "تم الإنشاء" : value}
            </Badge>
          );
        },
      },
      {
        Header: "الإجراءات",
        accessor: "actions",
        Cell: ({ row }) => (
          <Button
            color="primary"
            size="sm"
            onClick={() => viewDetails(row.original)}
            className="d-flex align-items-center"
          >
            <FiExternalLink className="me-1" />
            عرض
          </Button>
        ),
        width: 100
      },
    ],
    []
  );

  return (
    <div className="page-content" dir="rtl">
      <Container fluid>
        <Breadcrumbs
          title="بيانات التسويات"
          breadcrumbItem="أرشيف بيانات التسويات"
        />

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2">جاري تحميل البيانات...</p>
          </div>
        ) : error ? (
          <Alert color="danger">
            <h5 className="alert-heading">حدث خطأ!</h5>
            <p>{error}</p>
            <Button
              color="danger"
              size="sm"
              onClick={() => window.location.reload()}
            >
              إعادة المحاولة
            </Button>
          </Alert>
        ) : groupedSettlements.length === 0 ? (
          <Alert color="info" className="text-center">
            <FiFileText className="me-2" />
            لا توجد بيانات تسويات متاحة حالياً
          </Alert>
        ) : (
          <Card>
            <CardBody>
              {/* Date Range Filter */}
              <div className="mb-4 p-3 border rounded bg-light">
                <Row className="mb-3">
                  <Col md={12}>
                    <h6 className="mb-3">تصفية حسب التاريخ</h6>
                  </Col>
                </Row>
                
                {/* Quick Filter Buttons */}
                <Row className="mb-3">
                  <Col md={12}>
                    <div className="d-flex flex-wrap gap-2">
                      <Button 
                        size="sm" 
                        color={!startDate && !endDate ? "primary" : "outline-primary"}
                        onClick={() => handleQuickFilter('reset')}
                      >
                        كل البيانات
                      </Button>
                      <Button 
                        size="sm" 
                        color="outline-info"
                        onClick={() => handleQuickFilter('today')}
                      >
                        اليوم
                      </Button>
                      <Button 
                        size="sm" 
                        color="outline-info"
                        onClick={() => handleQuickFilter('yesterday')}
                      >
                        أمس
                      </Button>
                      <Button 
                        size="sm" 
                        color="outline-info"
                        onClick={() => handleQuickFilter('thisWeek')}
                      >
                        هذا الأسبوع
                      </Button>
                   
                    </div>
                  </Col>
                </Row>

                {/* Custom Date Range */}
                <Row>
                  <Col md={5}>
                    <FormGroup>
                      <Label>من التاريخ:</Label>
                      <Input
                        type="date"
                        value={startDate || ''}
                        onChange={(e) => setStartDate(e.target.value || null)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={5}>
                    <FormGroup>
                      <Label>إلى التاريخ:</Label>
                      <Input
                        type="date"
                        value={endDate || ''}
                        onChange={(e) => setEndDate(e.target.value || null)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2} className="d-flex align-items-end">
                    <FormGroup className="w-100">
                      <Button 
                        color="warning" 
                        size="sm"
                        className="w-100"
                        onClick={() => handleQuickFilter('reset')}
                      >
                        إعادة تعيين
                      </Button>
                    </FormGroup>
                  </Col>
                </Row>

                {(startDate || endDate) && (
                  <Alert color="info" className="mt-2 mb-0">
                    {startDate && endDate ? (
                      <span>عرض التسويات من {startDate} إلى {endDate}</span>
                    ) : startDate ? (
                      <span>عرض التسويات من {startDate}</span>
                    ) : (
                      <span>عرض التسويات حتى {endDate}</span>
                    )}
                    <span className="ms-2">({groupedSettlements.length} نتيجة)</span>
                  </Alert>
                )}
              </div>

              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">قائمة التسويات</h5>
                <Button 
                  color="success" 
                  onClick={exportToExcel}
                  className="d-flex align-items-center"
                >
                  <FiFile className="me-1" />
                  تصدير إلى Excel
                </Button>
              </div>
              <TableContainer
                columns={columns}
                data={groupedSettlements}
                isGlobalFilter={true}
                isAddOptions={false}
                customPageSize={10}
                className="custom-header-css"
              />
            </CardBody>
          </Card>
        )}

        <SettlementDetailsModal
          isOpen={isModalOpen}
          toggle={toggleModal}
          settlement={selectedSettlement}
          onExportExcel={exportSettlementToExcel}
        />
      </Container>
    </div>
  );
};

const SettlementDetailsModal = ({ isOpen, toggle, settlement, onExportExcel }) => {
  const settlementRef = useRef();
  const [loadingPDF, setLoadingPDF] = useState(false);

  const handlePrint = useReactToPrint({
    content: () => settlementRef.current,
    pageStyle: `
      @page {
        size: A4;
        margin: 10mm;
      }
      @media print {
        body {
          direction: rtl;
        }
        .settlement-header {
          border-bottom: 2px solid #000;
          padding-bottom: 15px;
          margin-bottom: 20px;
        }
        .settlement-details {
          background-color: #f9f9f9;
          padding: 15px;
          border-radius: 5px;
        }
      }
    `,
  });

  const formatDate = (date) => {
    if (!date) return 'غير متوفر';
    try {
      const parsedDate = typeof date === 'string' ? parseISO(date) : date;
      return format(parsedDate, 'yyyy/MM/dd - hh:mm a', { locale: ar });
    } catch (error) {
      console.error('Date parsing error:', error);
      return 'تاريخ غير صالح';
    }
  };

  const formatCurrency = (value) => {
    return `${(parseFloat(value) || 0).toFixed(2)} ر.س`;
  };

  const downloadPDF = () => {
    if (!settlement) return;

    const input = settlementRef.current;

    html2canvas(input, {
      scale: 2,
      logging: true,
      useCORS: true
    }).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = canvas.height * imgWidth / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`settlement_${settlement.id?.slice(0, 8) || 'N/A'}.pdf`);
    });
  };

  if (!settlement) return null;

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle} className="border-0">
        <span className="modal-title">بيان التسوية - Settlement Statement</span>
      </ModalHeader>
      <ModalBody>
        <div className="settlement-container" ref={settlementRef}>
          <div className="settlement-header text-center mb-4">
            <h2 className="settlement-title">بيان التسوية</h2>
            <p className="text-muted">Settlement Statement</p>
          </div>

          <img
            src="/logo.png"
            alt="Company Logo"
            style={{ height: 60, objectFit: "contain" }}
            className="mb-4 d-block mx-auto"
          />

          {/* Invoice From/To Header */}
          <div className="invoice-header mb-4">
            <h6 className="mb-3 text-center">فاتورة عمولة تسوية</h6>
            <Row>
              <Col md={6} className="mb-3">
                <div className="p-3 border rounded bg-light">
                  <h6 className="mb-2 text-primary">من (المنصة):</h6>
                  <p className="mb-1">
                    <strong>{settlement.platform?.company_name || "ﺷﺮﻛﺔ ﺍﻟﻔﻜﺮﺓ ﺍﻹﺑﺪﺍﻋﻴﻪ ﺍﻟﺘﺠﺎﺭﻳﺔ"}</strong>
                  </p>
                  <p className="mb-1">
                    <small className="text-muted">السجل التجاري:</small> {settlement.platform?.commercial_register || "غير متوفر"}
                  </p>
                  <p className="mb-1">
                    <small className="text-muted">رقم الضريبة:</small> {settlement.platform?.vat_number || "غير متوفر"}
                  </p>
                  <p className="mb-1">
                    <small className="text-muted">البلد:</small> {settlement.platform?.country || "غير متوفر"}
                  </p>
                  <p className="mb-0">
                    <small className="text-muted">العنوان:</small> {settlement.platform?.address || "غير متوفر"}
                  </p>
                </div>
              </Col>
              <Col md={6} className="mb-3">
                <div className="p-3 border rounded bg-light">
                  <h6 className="mb-2 text-success">إلى (مقدم الخدمة):</h6>
                  <p className="mb-1">
                    <strong>{settlement.service_provider?.name || "غير متوفر"}</strong>
                  </p>
                  <p className="mb-1">
                    <small className="text-muted">السجل التجاري:</small> {settlement.service_provider?.commercial_register || "غير متوفر"}
                  </p>
                  <p className="mb-1">
                    <small className="text-muted">رقم الضريبة:</small> {settlement.service_provider?.vat_number || "غير متوفر"}
                  </p>
                  <p className="mb-1">
                    <small className="text-muted">الهاتف:</small> {settlement.service_provider?.mobile || "غير متوفر"}
                  </p>
                  <p className="mb-0">
                    <small className="text-muted">البلد:</small> {settlement.service_provider?.country || "غير متوفر"}
                  </p>
                </div>
              </Col>
            </Row>
            {settlement.period_start && settlement.period_end && (
              <p className="mb-0 text-muted text-center mt-3">
                <small>الفترة: من {formatDate(settlement.period_start)} إلى {formatDate(settlement.period_end)}</small>
              </p>
            )}
          </div>

          {/* Invoice Period Section */}
          <Row className="mb-4">
            <Col md={6}>
              <div className="mb-3 p-3 border rounded bg-info bg-opacity-10">
                <h6 className="mb-2">فترة الفواتير المجمعة</h6>
                <p className="mb-1">
                  <strong>من تاريخ:</strong> {settlement.period_start ? formatDate(settlement.period_start) : "غير متوفر"}
                </p>
                <p className="mb-0">
                  <strong>إلى تاريخ:</strong> {settlement.period_end ? formatDate(settlement.period_end) : "غير متوفر"}
                </p>
              </div>
            </Col>
            <Col md={6} className="text-end">
              <div className="mb-3 p-3 border rounded bg-warning bg-opacity-10">
                <h6 className="mb-2">تفاصيل التجميع</h6>
                <p className="mb-1">
                  <strong>عدد الفواتير:</strong> {settlement.invoice_count || 1}
                </p>
                <p className="mb-0">
                  <strong>حالة التسوية:</strong> {settlement.is_batched ? "مجمعة" : "فردية"}
                </p>
              </div>
            </Col>
          </Row>

          {/* Header Information */}
          <Row className="mb-4">
            <Col md={6}>
              <div className="mb-3">
                <h5>معلومات مقدم الخدمة</h5>
                <p className="mb-1">
                  <strong>الاسم:</strong> {settlement.service_provider?.name || "غير متوفر"}
                </p>

                <p className="mb-1">
                  <strong>السجل التجاري:</strong> {settlement.service_provider?.commercial_register || "غير متوفر"}
                </p>
                <p className="mb-1">
                  <strong>رقم الضريبة:</strong> {settlement.service_provider?.vat_number || "غير متوفر"}
                </p>
                <p className="mb-1">
                  <strong>الهاتف:</strong> {settlement.service_provider?.mobile || "غير متوفر"}
                </p>
              </div>
            </Col>
            <Col md={6} className="text-end">
              <div className="mb-3">
                <h5>معلومات بيان التسوية</h5>
                <p className="mb-1">
                  <strong>رقم البيان:</strong> {settlement.id?.slice(0, 12) || "N/A"}
                </p>
                <p className="mb-1">
                  <strong>تاريخ الإنشاء:</strong> {formatDate(settlement.created_at)}
                </p>
                <p className="mb-1">
                  <strong>الحالة:</strong>
                  <Badge
                    color={settlement.settlement_type === "refund" || settlement.is_refund_settlement ? "danger" : "success"}
                    className="ms-2"
                  >
                    {settlement.settlement_type === "refund" || settlement.is_refund_settlement
                      ? "استرجاع"
                      : settlement.status === "generated"
                        ? "تم الإنشاء"
                        : settlement.status}
                  </Badge>
                </p>
                <p className="mb-1">
                  <strong>نوع التسوية:</strong>
                  <Badge
                    color={settlement.settlement_type === "refund" || settlement.is_refund_settlement ? "danger" : "success"}
                    className="ms-2"
                  >
                    {settlement.settlement_type === "refund" || settlement.is_refund_settlement ? "استرجاع" : "عادية"}
                  </Badge>
                </p>
                {settlement?.batch_settlement_id && (
                  <p className="mb-1">
                    <strong>تسوية مجمعة:</strong>
                    <Badge color="info" className="ms-2">
                      نعم
                    </Badge>
                    <span className="text-muted ms-2">
                      ({settlement.invoice_count || 1} فواتير)
                    </span>
                  </p>
                )}
              </div>
            </Col>

          </Row>

          {/* Financial Details */}
          <div className="settlement-details mb-4">
            <h5 className="mb-3">الملخص المالي - Financial Summary</h5>
            <Table bordered className="mb-0">
              <tbody>
                <tr>
                  <td><strong>المبلغ الأصلي للخدمة</strong><br/><small className="text-muted">Original Service Amount</small></td>
                  <td className="text-end">
                    <strong>{formatCurrency(settlement.amounts?.original_service_amount || settlement.amounts?.gross_service_amount)}</strong>
                  </td>
                </tr>
                {Number(settlement.amounts?.platform_commission || 0) > 0 && (
                  <tr className="table-warning">
                    <td><strong>عمولة المنصة (مخصومة)</strong><br/><small className="text-muted">Platform Commission (Deducted)</small></td>
                    <td className="text-end text-danger">
                      <strong>- {formatCurrency(settlement.amounts.platform_commission)}</strong>
                    </td>
                  </tr>
                )}
                {Number(settlement.amounts?.tamara_commission || 0) > 0 && (
                  <tr className="table-warning">
                    <td><strong>عمولة تمارا (مخصومة)</strong><br/><small className="text-muted">Tamara Commission (Deducted)</small></td>
                    <td className="text-end text-danger">
                      <strong>- {formatCurrency(settlement.amounts.tamara_commission)}</strong>
                    </td>
                  </tr>
                )}
                <tr className="table-info">
                  <td><strong>المبلغ بعد العمولة (قبل ض.ق.م)</strong><br/><small className="text-muted">Amount After Commission (Before VAT)</small></td>
                  <td className="text-end">
                    <strong>{formatCurrency(settlement.amounts?.amount_after_commission || settlement.amounts?.service_net)}</strong>
                  </td>
                </tr>
                <tr>
                  <td><strong>ضريبة القيمة المضافة (15%)</strong><br/><small className="text-muted">VAT (15%)</small></td>
                  <td className="text-end">
                    <strong>+ {formatCurrency(settlement.amounts?.service_vat)}</strong>
                  </td>
                </tr>
                <tr className="table-active">
                  <td><strong>الصافي مع الضريبة</strong><br/><small className="text-muted">Net Amount with VAT</small></td>
                  <td className="text-end">
                    <strong>{formatCurrency(
                      settlement.amounts?.net_amount_with_vat || 
                      (settlement.amounts?.amount_after_commission || settlement.amounts?.service_net || 0) * 1.15
                    )}</strong>
                  </td>
                </tr>
                {Number(settlement.amounts?.cancellation_fines || 0) > 0 && (
                  <>
                  <tr className="table-warning">
                    <td><strong>غرامات الإلغاء (مخصومة)</strong><br/><small className="text-muted">Cancellation Fines (Deducted)</small></td>
                    <td className="text-end text-danger">
                      <strong>- {formatCurrency(settlement.amounts.cancellation_fines)}</strong>
                    </td>
                  </tr>
                  <tr className="table-light">
                    <td><strong>الغرامة خصمت من</strong><br/><small className="text-muted">Fine Charged To</small></td>
                    <td className="text-end">
                      <Badge color={settlement.fine_charged_to === "app_user" ? "info" : "warning"}>
                        {settlement.fine_charged_to === "app_user" ? "العميل / App User" : "مقدم الخدمة / Service Provider"}
                      </Badge>
                    </td>
                  </tr>
                  {settlement.fine_reason && (
                    <tr className="table-light">
                      <td><strong>سبب الخصم</strong><br/><small className="text-muted">Deduction Reason</small></td>
                      <td className="text-end">
                        {settlement.fine_reason}
                      </td>
                    </tr>
                  )}
                  </>
                )}
                <tr className="table-success">
                  <td>
                    <strong>
                      {settlement.settlement_type === "refund" || settlement.is_refund_settlement
                        ? "المبلغ النهائي للاسترجاع"
                        : "المبلغ النهائي المستحق"}
                    </strong>
                    <br/>
                    <small className="text-muted">
                      {settlement.settlement_type === "refund" || settlement.is_refund_settlement
                        ? "Refund Amount (Not Counted as Revenue)"
                        : "Final Payout Amount"}
                    </small>
                  </td>
                  <td className="text-end">
                    <strong style={{ fontSize: '1.1em', color: (settlement.settlement_type === "refund" || settlement.is_refund_settlement) ? '#dc3545' : '#198754' }}>
                      {formatCurrency(
                        settlement.amounts?.final_payout ?? 
                        ((settlement.amounts?.net_amount_with_vat || settlement.amounts?.net_after_fines || 0) - 
                        (settlement.amounts?.cancellation_fines || 0))
                      )}
                    </strong>
                    {(settlement.settlement_type === "refund" || settlement.is_refund_settlement) && (
                      <div><small className="text-muted">غير محتسب ضمن إيرادات المنصة</small></div>
                    )}
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>

          {/* Deductions Details */}
          {Array.isArray(settlement.deductions) && settlement.deductions.length > 0 && (
            <div className="settlement-details mb-4">
              <h5 className="mb-3">الخصومات المطبقة</h5>
              <Table bordered className="mb-0">
                <thead>
                  <tr>
                    <th>النوع</th>
                    <th>السبب</th>
                    <th className="text-end">المبلغ</th>
                  </tr>
                </thead>
                <tbody>
                  {settlement.deductions.map((ded, idx) => (
                    <tr key={idx}>
                      <td>{ded.type === "cancellation_fine" ? "غرامة إلغاء" : ded.type || "خصم"}</td>
                      <td>{ded.reason || settlement.fine_reason || "غير محدد"}</td>
                      <td className="text-end text-danger">{formatCurrency(ded.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          {/* Transaction References */}
          <div className="settlement-details mb-4">
            <h5 className="mb-3">المراجع والمعلومات الإضافية</h5>
            <Row>
              <Col md={6}>
                <p>
                  <strong>معرف الحجز:</strong> {settlement.booking_id || "غير متوفر"}
                </p>
                <p>
                  <strong>معرف الفاتورة:</strong> {settlement.invoice_id || "غير متوفر"}
                </p>
              </Col>
              <Col md={6} className="text-end">
                {/* <p>
                  <strong>معرف فاتورة العمولة:</strong> {settlement.erp_commission_invoice_id || "غير متوفر"}
                </p> */}
                <p>
                  <strong>تاريخ إنشاء البيان:</strong> {formatDate(settlement.created_at)}
                </p>
              </Col>
            </Row>
          </div>

          {/* Footer */}
          <div className="settlement-footer mt-4 pt-4 border-top">
            <p className="text-muted text-center">شكراً لاستخدامك خدماتنا</p>
            <p className="text-center text-muted" style={{ fontSize: "12px" }}>
              تم إنشاء هذا البيان بواسطة نظام إدارة ﺷﺮﻛﺔ ﺍﻟﻔﻜﺮﺓ ﺍﻹﺑﺪﺍﻋﻴﻪ ﺍﻟﺘﺠﺎﺭﻳﺔ
            </p>
          </div>
        </div>
      </ModalBody>
      <ModalFooter className="border-0">
        <Button color="secondary" onClick={toggle}>
          إغلاق
        </Button>
        <ButtonGroup>
          <Button color="info" onClick={() => onExportExcel && onExportExcel(settlement)}>
            <FiFile className="me-1" />
            Excel
          </Button>
          <Button color="success" onClick={downloadPDF}>
            <FiDownload className="me-1" />
            PDF
          </Button>
        </ButtonGroup>
      </ModalFooter>
    </Modal>
  );
};

export default SettlementStatements;
